import type { ServiceRole } from '@clockwise/clients/service-roles';
import { useCallback } from 'react';
import { CheckSlot, Combobox } from '@/components/custom/combobox';
import { PickerLoadError } from '@/components/custom/picker-load-error';
import { PickerTruncationNotice } from '@/components/custom/picker-truncation-notice';
import { ServiceRoleLabel } from '@/components/custom/service-role-label';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';
import { Skeleton } from '@/components/ui/skeleton';
import { serviceRolesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

// Picker for a service role. Defaults to 'preload' — one page of up to
// SERVICE_ROLE_PRELOAD_LIMIT roles loads on first open and filters in
// memory, so every role is visible without typing (the lazy default showed
// only the first 20 until the user searched). If a tenant ever outgrows the
// cap, the dropdown renders a "first N of M" footer instead of silently
// hiding the rest.
//
// Note: unfiltered by default — the sheets that pick a service role show the
// full catalog (an existing membership may reference a now-inactive role),
// matching the Select this replaced. Callers that only ever want live roles
// (activities) opt in with `active`.
interface Props {
  value: string | null;
  onChange: (serviceRole: ServiceRole | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  id?: string;
  loadStrategy?: LoadStrategy;
  /**
   * Restrict the catalog to customer-facing (`false`) or back-office (`true`)
   * roles. Omit for the full catalog, which stays the default so existing
   * callers are unaffected.
   *
   * Budgets pass `false`: an internal role is non-billable back-office work
   * and can't carry a customer budget, the same rule ratecards applies.
   */
  internal?: boolean;
  /**
   * Restrict the catalog to active (`true`) or retired (`false`) roles. Omit
   * for the full catalog. An already-selected role is resolved by id, so
   * filtering here never blanks out a value that points at a retired role.
   */
  active?: boolean;
  /** Roles to hide from the dropdown — e.g. ones the caller has already
   *  picked into a multi-select. Folded into the lazy page size by the hook. */
  excludeIds?: readonly string[];
}

// One full page of the role catalog. Must stay at or below the server cap
// (service_roles `findSchema` caps `limit` at 500; the domain `maxLimit`
// matches) — going above 400s the query.
const SERVICE_ROLE_PRELOAD_LIMIT = 500;

/**
 * The exact list query this picker preloads with. Exported so a screen that
 * also needs the catalog in its own hands (e.g. to label already-picked
 * roles, or to offer "Select all") can build the identical query key and
 * share one cache entry with the dropdown instead of firing a second,
 * near-duplicate fetch that could disagree about which roles exist.
 */
export function serviceRolePreloadQuery(opts: { internal?: boolean; active?: boolean } = {}) {
  return roleListQuery({ ...opts, limit: SERVICE_ROLE_PRELOAD_LIMIT });
}

const labelOf = (r: ServiceRole) => r.name;
const getRoleId = (r: ServiceRole) => r.id;
const roleFilter = (r: ServiceRole, q: string) => {
  const hay = `${r.name} ${r.shortCode}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};
// `internal`/`active` ride in the query object AND therefore in the query key,
// so a filtered picker can never read a cached unfiltered list (or vice versa).
const roleListQuery = ({ q, limit, internal, active }: RoleListArgs) => ({
  q,
  internal,
  active,
  sortField: 'name' as const,
  sortDirection: 'asc' as const,
  limit,
  offset: 0,
});
type RoleListArgs = { q?: string; limit: number; internal?: boolean; active?: boolean };
const fetchRoleOne = (id: string) => serviceRolesApi.get(id);
const roleDetailKey = (id: string) => queryKeys.serviceRoles.detail(id);

export function ServiceRoleAutocomplete({
  value,
  onChange,
  disabled,
  placeholder = 'Select service role…',
  className,
  hasError,
  id,
  loadStrategy = 'preload',
  internal,
  active,
  excludeIds,
}: Props) {
  // `useEntityAutocomplete` hands its callbacks only `{ q, limit }`, so the
  // filters are closed over here. Memoized on them so a re-render doesn't
  // churn the hook's identities.
  const fetchList = useCallback(
    (o: { q?: string; limit: number }) =>
      serviceRolesApi.list(roleListQuery({ ...o, internal, active })),
    [internal, active],
  );
  const listKey = useCallback(
    (o: { q?: string; limit: number }) =>
      queryKeys.serviceRoles.list(roleListQuery({ ...o, internal, active })),
    [internal, active],
  );

  const auto = useEntityAutocomplete<ServiceRole>({
    value,
    strategy: loadStrategy,
    excludeCount: excludeIds?.length ?? 0,
    fetchList,
    fetchOne: fetchRoleOne,
    listKey,
    detailKey: roleDetailKey,
    preloadLimit: SERVICE_ROLE_PRELOAD_LIMIT,
  });

  const exclude = new Set(excludeIds ?? []);
  const items = exclude.size === 0 ? auto.items : auto.items.filter((r) => !exclude.has(r.id));

  // A picker mounted with a value (any edit form) can't show a label until
  // the detail fetch lands, and until then the input is empty — on a
  // required field that reads as "nothing selected", not "loading", and
  // invites the user to pick a replacement for a role that is in fact set.
  // Show the loading state explicitly instead (G15).
  if (auto.resolvingValue) {
    return <Skeleton className={cn('h-9 w-full', className)} />;
  }

  return (
    <Combobox<ServiceRole>
      id={id}
      items={items}
      selectedItem={auto.selectedItem}
      value={value}
      onChange={onChange}
      getId={getRoleId}
      getLabel={labelOf}
      filterFn={auto.isClient ? roleFilter : undefined}
      onQueryChange={auto.isClient ? undefined : auto.setQuery}
      onOpenChange={auto.setOpen}
      loading={auto.loading}
      // A refused or failed catalog fetch says so where the list would be,
      // rather than opening on "No matches" as though there were no roles.
      renderEmpty={
        auto.error ? () => <PickerLoadError error={auto.error} noun="service roles" /> : undefined
      }
      placeholder={placeholder}
      disabled={disabled}
      hasError={hasError}
      className={className}
      renderItem={(r, { isSelected }) => (
        // Short code BEFORE the name, matching ProductLabel and
        // ProductAutocomplete — this picker was the only place in the app that
        // trailed it, which made the codes impossible to scan down a column.
        // ServiceRoleLabel owns that composition now, so the dropdown row and
        // every other place a role is rendered can't drift apart.
        <div className="flex min-w-0 items-center gap-1.5">
          <CheckSlot isSelected={isSelected} />
          <ServiceRoleLabel code={r.shortCode || null} name={r.name} className="font-medium" />
        </div>
      )}
      // Catalog outgrew the preload page: say so instead of silently
      // presenting a partial list as complete.
      renderFooter={
        auto.truncated
          ? () => (
              <PickerTruncationNotice
                truncated={auto.truncated}
                noun="service roles"
                clientFiltered={auto.isClient}
              />
            )
          : undefined
      }
    />
  );
}
